import {
  listDatasetUploads,
  type DatasetUploadJob,
  type DatasetUploadListScope,
  type UploadStatusGroup,
} from "@/api";
import {
  DataTableSource,
  type DataTablePageOptions,
  type DataTablePageReq,
  type DataTablePageResp,
} from "@carbon6/c6-frontend-design";
import {
  createDatasetUploadTableRows,
  type DatasetUploadTableRow,
  type DatasetUploadTableRowActionBuilder,
  type DatasetUploadTableRowClickHandler,
} from "./DatasetUploadTableRow";

type DatasetUploadTableSourceOptions = {
  pageSize: number;
  rowActionsBuilder?: DatasetUploadTableRowActionBuilder | null;
  rowClickHandler?: DatasetUploadTableRowClickHandler | null;
  resolveErrorMessage: (error: unknown) => string;
  // URL 에서 읽은 첫 조건. 첫 로드에 바로 적용한다. 그래서 초기 조회 뒤 setter 는 no-op 이 된다.
  initialScope?: DatasetUploadListScope;
  initialStatusGroup?: UploadStatusGroup;
};

export class DatasetUploadTableSource extends DataTableSource<DatasetUploadTableRow> {
  private readonly rowActionsBuilder: DatasetUploadTableRowActionBuilder | null;
  private readonly rowClickHandler: DatasetUploadTableRowClickHandler | null;
  // 목록 소유 범위. 기본값은 내 업로드이며 all은 관리자 화면에서만 선택한다.
  private scope: DatasetUploadListScope;
  // coarse 상태 그룹. "all" 은 필터 없음이다(status_group 을 보내지 않는다). 서버가 멤버 status 로 펼친다(§3).
  private statusGroup: UploadStatusGroup;

  constructor({
    pageSize,
    rowActionsBuilder = null,
    rowClickHandler = null,
    resolveErrorMessage,
    initialScope = "me",
    initialStatusGroup = "all",
  }: DatasetUploadTableSourceOptions) {
    super({
      initialPageSize: pageSize,
      resolveErrorMessage,
      prefetchMode: "paging",
    });
    this.rowActionsBuilder = rowActionsBuilder;
    this.rowClickHandler = rowClickHandler;
    this.scope = initialScope;
    this.statusGroup = initialStatusGroup;
  }

  setScope(scope: DatasetUploadListScope): Promise<void> {
    if (scope === this.scope) {
      return Promise.resolve();
    }
    this.scope = scope;
    return this.goToPage(1);
  }

  setStatusGroup(group: UploadStatusGroup): Promise<void> {
    if (group === this.statusGroup) {
      return Promise.resolve();
    }
    this.statusGroup = group;
    return this.goToPage(1);
  }

  protected override cacheSignature(): string | null {
    return JSON.stringify([this.scope, this.statusGroup]);
  }

  protected override async onPage(
    req: DataTablePageReq,
    { signal }: DataTablePageOptions,
  ): Promise<DataTablePageResp<DatasetUploadTableRow>> {
    const response = await listDatasetUploads(
      {
        page: req.page,
        size: req.size,
        scope: this.scope,
        ...(this.statusGroup !== "all" ? { status_group: this.statusGroup } : {}),
      },
      { signal },
    );

    return {
      rows: this.createRows(response.items),
      total: response.total,
      page: response.page,
      size: response.size,
    };
  }

  private createRows(jobs: readonly DatasetUploadJob[]): DatasetUploadTableRow[] {
    return createDatasetUploadTableRows(jobs, this.rowActionsBuilder, this.rowClickHandler);
  }
}
